// The stock take in progress, kept in the station's own database (`device_state`), so a
// count survives a reload, a sign-out or a flat battery (a reload signs staff out, D-046).
// One count at a time per station. In memory it is a small store React reads with
// useSyncExternalStore; every change is written through, in order.

import {
  DEVICE_STATE_KEYS,
  Mutex,
  readDeviceState,
  writeDeviceState,
  type StationData,
} from '@enp-suite/data';
import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { getStationData } from '../data/station-data-store.js';
import { useBlindHoldLocations } from '../stock/awaiting-figures.js';
import { addMovement, movementOf, type CountDraft } from './count-model.js';

/** undefined = not read yet; null = no count in progress. */
let current: CountDraft | null | undefined;
let loading: Promise<void> | undefined;
const listeners = new Set<() => void>();
const writes = new Mutex();

function notify() {
  for (const l of listeners) l();
}

async function load(): Promise<void> {
  const data = getStationData();
  if (!data) return;
  const stored = await readDeviceState<CountDraft>(data.engine, DEVICE_STATE_KEYS.stockTakeDraft);
  // A write that happened meanwhile wins over what was read.
  if (current === undefined) {
    current = stored && Array.isArray(stored.lines) ? stored : null;
    notify();
  }
}

/** Replaces the count in progress (null ends it) and writes it through. */
export function setCountDraft(next: CountDraft | null): Promise<void> {
  current = next;
  notify();
  const data = getStationData();
  if (!data) return Promise.resolve();
  return writes.run(() =>
    data.engine.writeTransaction((tx) =>
      writeDeviceState(tx, DEVICE_STATE_KEYS.stockTakeDraft, next, new Date()),
    ),
  );
}

/** Applies `change` to the count in progress (nothing when there is none). */
export function updateCountDraft(change: (draft: CountDraft) => CountDraft): Promise<void> {
  if (!current) return Promise.resolve();
  const next = change(current);
  if (next === current) return Promise.resolve();
  return setCountDraft(next);
}

export function getCountDraft(): CountDraft | null | undefined {
  return current;
}

/** The count in progress: undefined while it is being read, null when there is none. */
export function useCountDraft(): CountDraft | null | undefined {
  const draft = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => current,
  );
  useEffect(() => {
    if (current === undefined && !loading) {
      loading = load().finally(() => {
        loading = undefined;
      });
    }
  }, []);
  return draft;
}

/** Whether a blind count is running on this station (other views hide stock figures). */
export function useBlindCountActive(): boolean {
  const draft = useCountDraft();
  return draft?.blind === true;
}

/** Which stock figures the station hides (D-049, D-057). */
export interface HiddenFigures {
  /** A blind count runs on this station: every figure is hidden. */
  all: boolean;
  /** Whether figures at `locationId` are hidden (a blind count runs, or ran there lately). */
  hides: (locationId: string | null | undefined) => boolean;
  /** Any location is hidden. */
  any: boolean;
}

/**
 * The figures to hide: all of them while a blind count runs here; afterwards the counted
 * location's, until the server's figures after the count have synced (or for 30 minutes
 * after a discarded count), D-057.
 */
export function useHiddenFigures(): HiddenFigures {
  const all = useBlindCountActive();
  const held = useBlindHoldLocations();
  const hides = useCallback(
    (locationId: string | null | undefined) =>
      all || (locationId !== null && locationId !== undefined && held.has(locationId)),
    [all, held],
  );
  return useMemo(() => ({ all, hides, any: all || held.size > 0 }), [all, hides, held]);
}

/**
 * Notes every move and write-off saved on this station at the count's location while a count
 * runs (CR4-6), so the review can ask for the lines counted before it to be counted again.
 */
export function watchCountMovements(data: StationData): () => void {
  return data.onEnqueued((envelope) => {
    void (async () => {
      if (current === undefined) await (loading ?? load());
      const draft = current;
      if (!draft) return;
      const movement = movementOf(draft, envelope);
      if (movement) await updateCountDraft((d) => addMovement(d, movement));
    })().catch((err: unknown) => console.error('The count could not note a move', err));
  });
}

/** For tests and a station wipe: forget the in-memory copy. */
export function resetCountDraftCache(): void {
  current = undefined;
  notify();
}
